/* liveArenaEngine — pure-logic pieces of the LiveArena tap-battle scene.
 *
 * Extracted from LiveArena.jsx to keep that page under 1000 lines and to
 * isolate the reducer + event-queue building so they can be unit-tested
 * (or reused by AI-simulator tooling later).
 *
 * NOTHING in this file touches React state, DOM or JSX. The reducer
 * returns plain immutable state objects that the component feeds into
 * useReducer.
 */

export const TAP_COST_ATTACK = 3;
export const TAP_COST_RESULT = 1;
export const ULTIMATE_TAP_THRESHOLD = 60;
export const CONSUMABLE_FREEZE_MS = 1600;
export const BATTLE_TIMER_SECONDS = 180;
export const COMBO_TIERS = [10, 25, 50, 100];

// --- Perfect-Tap Timing ---------------------------------------------
// After every "resolved" tap that advances the queue (attack/result),
// a cooldown window opens. Taps that land inside the SWEET-SPOT band
// award a "PERFECT TAP" bonus (extra ultimate charge + combo). Missed
// taps break the perfect streak.
export const PERFECT_COOLDOWN_MS = 900;      // total ring duration
export const PERFECT_WINDOW_START_MS = 630;  // sweet-spot opens (70%)
export const PERFECT_WINDOW_END_MS = 900;    // sweet-spot closes (100%)
export const PERFECT_ULTIMATE_BONUS = 4;     // extra ult charge per perfect
export const PERFECT_COMBO_BONUS = 2;        // extra combo per perfect

// -----------------------------------------------------------------------
//  Event queue building
// -----------------------------------------------------------------------
export function classify(line) {
  const l = (line || '').toLowerCase();
  if (l.startsWith('— round')) return 'round_intro';
  if (l.includes('combo!')) return 'combo';
  if (l.startsWith('— ') && l.includes('takes the round')) return 'round_win';
  if (l.startsWith('— ')) return 'flavor';
  if (l.includes('lunges') || l.includes('strikes') || l.includes('charges')
      || l.includes('attacks') || l.includes('unleashes')) return 'attack';
  if (l.includes('critical') || l.includes('crushes')) return 'crit';
  if (l.includes('slips') || l.includes('dodges') || l.includes('evades')
      || l.includes('melts')) return 'dodge';
  if (l.includes('goes wide') || l.includes('miss')) return 'miss';
  if (l.includes('counter')) return 'counter';
  if (l.includes('frozen and')) return 'skip';
  return 'result';
}

export function buildEventQueue(battle) {
  const q = [];
  const rounds = battle?.rounds || [];
  rounds.forEach((rd, ri) => {
    (rd.log || []).forEach((line) => {
      const kind = classify(line);
      q.push({
        round: rd.round ?? ri + 1,
        line,
        kind,
        tap_cost: kind === 'attack' ? TAP_COST_ATTACK : TAP_COST_RESULT,
      });
    });
    q.push({
      round: rd.round ?? ri + 1,
      kind: 'round_end',
      hp_after: rd.hp_after,
      status_after: rd.status_after,
      abilities: rd.abilities,
      winner: rd.winner,
      tap_cost: 0,
    });
  });
  q.push({ kind: 'finale', tap_cost: 0 });
  return q;
}

// -----------------------------------------------------------------------
//  Reducer + advance
// -----------------------------------------------------------------------
export const INITIAL = (battle) => ({
  queue: buildEventQueue(battle),
  cursor: 0,
  tapEnergy: 0,
  ultimateCharge: 0,
  ultimateFired: false,
  comboCounter: 0,
  comboTierIndex: -1,
  screenShakeKey: 0,
  screenFlash: null,
  slowMoUntil: 0,
  frozenUntil: 0,
  logLines: [],
  hp: { ch: 1.0, op: 1.0 },
  status: { ch: [], op: [] },
  abilities: { ch: [], op: [] },
  currentRound: 1,
  finished: false,
  winnerRoundWins: null,
  lastLineKind: null,
  // Perfect-Tap Timing state — set every time an action advances.
  perfectCooldownStart: 0,   // timestamp (ms) — 0 means "no active cooldown"
  perfectCount: 0,           // total successful perfect taps this battle
  perfectStreak: 0,          // current uninterrupted streak
  perfectFlashKey: 0,        // bumped every perfect for FX
});

function advance(state) {
  const ev = state.queue[state.cursor];
  if (!ev) return { ...state, finished: true };
  const nextCursor = state.cursor + 1;
  // Every non-finale advance opens a new perfect-tap cooldown window.
  // The finale explicitly CLOSES the window so post-battle taps never
  // register as "perfect" flashes.
  const isFinale = ev.kind === 'finale';
  let s = {
    ...state,
    cursor: nextCursor,
    tapEnergy: 0,
    lastLineKind: ev.kind,
    perfectCooldownStart: isFinale ? 0 : Date.now(),
  };
  if (isFinale) return { ...s, finished: true, perfectStreak: 0 };
  if (ev.kind === 'round_end') {
    const hpMax = ev.hp_after ? {
      ch: ev.hp_after.challenger / (ev.hp_after.challenger + (ev.hp_after.opponent || 1) + 1e-6),
      op: ev.hp_after.opponent  / (ev.hp_after.challenger + (ev.hp_after.opponent || 1) + 1e-6),
    } : s.hp;
    return {
      ...s,
      hp: hpMax,
      status: ev.status_after
        ? { ch: ev.status_after.challenger || [], op: ev.status_after.opponent || [] }
        : s.status,
      abilities: ev.abilities
        ? { ch: ev.abilities.challenger || [], op: ev.abilities.opponent || [] }
        : s.abilities,
      currentRound: (ev.round || s.currentRound) + 1,
    };
  }
  const flash = ev.kind === 'crit' ? 'crit'
             : ev.kind === 'dodge' ? 'dodge'
             : ev.kind === 'combo' ? 'combo'
             : null;
  const shake = ev.kind === 'crit' ? state.screenShakeKey + 1 : state.screenShakeKey;
  const line = { text: ev.line, kind: ev.kind };
  const linesNext = [line, ...state.logLines].slice(0, 40);
  return { ...s, screenFlash: flash || s.screenFlash,
           screenShakeKey: shake, logLines: linesNext };
}

export function reducer(state, action) {
  switch (action.type) {
    case 'INIT': return INITIAL(action.battle);
    case 'TAP': {
      if (state.finished) return state;
      if (Date.now() < state.frozenUntil) return state;
      const ev = state.queue[state.cursor];
      if (!ev) return state;

      // -------- Perfect-Tap Timing detection --------
      // A perfect tap requires an ACTIVE cooldown window AND lands
      // inside the sweet-spot. Every advance opens the window; every
      // successful advance resets it. A tap OUTSIDE the sweet-spot
      // breaks the current perfect-streak (still counts as a tap).
      const now = Date.now();
      const elapsed = state.perfectCooldownStart ? (now - state.perfectCooldownStart) : Infinity;
      const inWindow = elapsed >= PERFECT_WINDOW_START_MS && elapsed <= PERFECT_WINDOW_END_MS;
      const cooldownExpired = elapsed > PERFECT_COOLDOWN_MS;
      const isPerfect = inWindow && !cooldownExpired;
      let perfectDelta = 0;
      let perfectStreakNext = state.perfectStreak;
      let perfectFlashKey = state.perfectFlashKey;
      if (isPerfect) {
        perfectDelta = 1;
        perfectStreakNext = state.perfectStreak + 1;
        perfectFlashKey = state.perfectFlashKey + 1;
      } else if (state.perfectCooldownStart && !cooldownExpired) {
        // Tap landed too early — break the streak, but not the count.
        perfectStreakNext = 0;
      }

      const bonusUlt = isPerfect ? PERFECT_ULTIMATE_BONUS : 0;
      const bonusCombo = isPerfect ? PERFECT_COMBO_BONUS : 0;
      const nextEnergy = state.tapEnergy + 1;
      const newUlt = Math.min(ULTIMATE_TAP_THRESHOLD, state.ultimateCharge + 1 + bonusUlt);
      const newCombo = state.comboCounter + 1 + bonusCombo;
      let nextTierIndex = state.comboTierIndex;
      let flash = isPerfect ? 'perfect' : null;
      for (let i = state.comboTierIndex + 1; i < COMBO_TIERS.length; i++) {
        if (newCombo >= COMBO_TIERS[i]) { nextTierIndex = i; flash = 'combo'; }
      }
      if (nextEnergy >= (ev.tap_cost || 1)) {
        return advance({ ...state, tapEnergy: 0, ultimateCharge: newUlt,
                          comboCounter: newCombo, comboTierIndex: nextTierIndex,
                          screenFlash: flash || state.screenFlash,
                          perfectCount: state.perfectCount + perfectDelta,
                          perfectStreak: perfectStreakNext,
                          perfectFlashKey });
      }
      return { ...state, tapEnergy: nextEnergy, ultimateCharge: newUlt,
               comboCounter: newCombo, comboTierIndex: nextTierIndex,
               screenFlash: flash || state.screenFlash,
               perfectCount: state.perfectCount + perfectDelta,
               perfectStreak: perfectStreakNext,
               perfectFlashKey };
    }
    case 'ADVANCE': return advance(state);
    case 'BREAK_COMBO': return { ...state, comboCounter: 0 };
    case 'FIRE_ULTIMATE': {
      if (state.ultimateFired || state.ultimateCharge < ULTIMATE_TAP_THRESHOLD) return state;
      let s = { ...state, ultimateFired: true, ultimateCharge: 0,
                screenFlash: 'ultimate', slowMoUntil: Date.now() + 700 };
      for (let i = 0; i < 5; i++) s = advance(s);
      return s;
    }
    case 'FIRE_ABILITY': {
      if (state.finished) return state;
      return advance({ ...state, screenFlash: 'combo',
                       comboCounter: state.comboCounter + 3 });
    }
    case 'USE_CONSUMABLE':
      return { ...state, frozenUntil: Date.now() + CONSUMABLE_FREEZE_MS,
               logLines: [{ text: `— ${action.item.name} consumed —`, kind: 'flavor' },
                          ...state.logLines].slice(0, 40),
               screenFlash: 'combo' };
    case 'FAST_FORWARD': {
      let s = state;
      let safety = 500;
      while (!s.finished && s.queue[s.cursor] && safety-- > 0) s = advance(s);
      return s;
    }
    case 'CLEAR_FLASH': return { ...state, screenFlash: null };
    case 'AUTO_TAP': return reducer(state, { type: 'TAP' });
    default: return state;
  }
}
